import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLogs } from '../../store/actions/userActions';
import { calculateUserRole } from '../../utils/utils';
import {
  PageHeader,
  Card,
  Badge,
  Btn,
  SearchBox,
  Select,
  Loading,
  EmptyState,
  ErrorState,
} from '../ForBrands/ui/BrandUI';
import '../ForBrands/brandMode.css';

// Admin Mode > Logs (/admin/logs). The API log: every create, update and
// delete request, newest first. AdminModeLayout draws the frame, the tab bar
// and the gutter, so this page draws only its header, filters and table.

const EMPTY = [];
const PAGE_SIZES = [5, 10, 20, 50];

// The same role tones the V2 Users table uses (BrandUI Badge tones).
export const ROLE_TONE = {
  'Athlete': 'brand', 'Coach': 'dark', 'Brand/Agency': 'soft', 'Rep / Agent': 'soft', 'Admin': 'outline',
  'Cache AI': 'outline', 'Intern': 'passive', 'Supporter': 'passive', 'Recruiter/Scout': 'passive', 'AD/PD': 'passive',
};

// Log timestamps come from the server in UTC without a zone suffix
// ("2026-09-28T14:05:09.123000"). Read them as UTC and show local time.
export const formatLogTime = (raw) => {
  if (!raw) return '';
  const date = new Date(`${String(raw).split('.')[0].replace(/Z$/, '')}Z`);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// fetchLogs logs a failed request and resolves anyway, so the page watches
// for the FETCH_LOGS it dispatches on success to tell a load from a failure.
// Resolves true when the logs arrived. When a newer request has started
// (isCurrent() is false), the answer is dropped so an older page that answers
// last cannot replace the rows of the page the admin is on.
const loadLogs = (condition, isCurrent = () => true) => (dispatch, getState) => {
  let loaded = false;
  const watch = (action) => {
    if (action && action.type === 'FETCH_LOGS') {
      loaded = true;
      if (!isCurrent()) return action;
    }
    return dispatch(action);
  };
  return Promise.resolve(fetchLogs(condition)(watch, getState))
    .then(() => loaded)
    .catch(() => false);
};

const cellWidth = (id) => (
  id === 'rowNumber' ? 'w-[48px]' :
  id === 'user' ? 'max-w-[240px]' :
  id === 'name' ? 'max-w-[180px]' :
  id === 'role' ? 'w-[132px]' :
  id === 'method' ? 'w-[96px]' :
  id === 'path' ? 'max-w-[280px]' :
  id === 'timestamp' ? 'w-[190px]' :
  'max-w-[160px]'
);

const LogViewer = () => {
  const dispatch = useDispatch();
  const allUsers = useSelector((state) => state.user?.allUsers || EMPTY);
  const logs = useSelector((state) => state.user?.logs || EMPTY);
  const totalCounts = useSelector((state) => state.user?.logTotalCounts || 0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('loading');
  const requestRef = useRef(0);

  // Only the newest request decides what the page shows.
  const load = useCallback((nextPage) => {
    requestRef.current += 1;
    const id = requestRef.current;
    const isCurrent = () => requestRef.current === id;
    setStatus('loading');
    dispatch(loadLogs({ page: nextPage, limit, search: searchQuery }, isCurrent)).then((ok) => {
      if (isCurrent()) setStatus(ok ? 'ready' : 'error');
    });
  }, [dispatch, limit, searchQuery]);

  useEffect(() => {
    setPage(1);
    load(1);
  }, [load]);

  // Leaving the page drops any answer still on its way.
  useEffect(() => () => { requestRef.current += 1; }, []);

  const handlePageClick = ({ selected }) => {
    const next = selected + 1;
    setPage(next);
    load(next);
  };

  const usersByEmail = useMemo(() => {
    const map = new Map();
    allUsers.forEach((u) => { if (u?.email) map.set(u.email, u); });
    return map;
  }, [allUsers]);

  const columns = useMemo(() => [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => <span className="text-bm-tertiary">{(page - 1) * limit + row.index + 1}</span>,
    },
    {
      id: 'user',
      header: 'Email',
      accessorKey: 'user',
      cell: ({ getValue }) => <span title={getValue() || ''}>{getValue() || ''}</span>,
    },
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => (row.user ? usersByEmail.get(row.user)?.name || '' : ''),
    },
    {
      id: 'role',
      header: 'Role',
      accessorFn: (row) => {
        if (!row.user) return '';
        return calculateUserRole(usersByEmail.get(row.user)?.permissions || []) || '';
      },
      cell: ({ getValue }) => {
        const roleName = getValue();
        return roleName ? <Badge tone={ROLE_TONE[roleName] || 'outline'}>{roleName}</Badge> : null;
      },
    },
    {
      id: 'method',
      header: 'Action',
      accessorKey: 'method',
      cell: ({ getValue }) => <span className="bm-label-xs uppercase tracking-[0.06em]">{getValue() || ''}</span>,
    },
    {
      id: 'path',
      header: 'Endpoint',
      accessorKey: 'path',
      cell: ({ getValue }) => <span title={getValue() || ''}>{getValue() || ''}</span>,
    },
    {
      id: 'timestamp',
      header: 'Date',
      accessorFn: (row) => formatLogTime(row.timestamp),
      cell: ({ getValue }) => <span className="text-bm-muted">{getValue()}</span>,
    },
  ], [usersByEmail, page, limit]);

  const pageCount = Math.ceil(totalCounts / limit);

  const table = useReactTable({
    data: logs,
    columns,
    pageCount,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => row._id || `log-${index}`,
  });

  const renderPagination = () => {
    if (pageCount <= 0) return null;
    return (
      <div className="min-w-0 max-w-full overflow-x-auto">
        <ReactPaginate
          previousLabel={"← Prev"}
          nextLabel={"Next →"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName="flex items-center gap-1 bm-label-xs"
          pageClassName="rounded-full border border-bm-border"
          pageLinkClassName="inline-flex h-8 min-w-[32px] items-center justify-center px-2 text-bm-ink"
          activeClassName="!border-bm-n700 bg-bm-n700 text-white"
          activeLinkClassName="text-white"
          previousClassName="rounded-full border border-bm-border"
          previousLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
          nextClassName="rounded-full border border-bm-border"
          nextLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
          breakLinkClassName="inline-flex h-8 items-center justify-center px-2 text-bm-tertiary"
          disabledClassName="opacity-40 cursor-not-allowed"
          forcePage={Math.min(page - 1, pageCount - 1)}
        />
      </div>
    );
  };

  const summary = status === 'ready'
    ? `${totalCounts} ${totalCounts === 1 ? 'event' : 'events'} · page ${page} of ${Math.max(1, pageCount)}`
    : '';

  let body;
  if (status === 'loading') {
    body = (
      <div role="status">
        <Loading label="Loading logs..." />
      </div>
    );
  } else if (status === 'error') {
    body = (
      <div className="flex flex-col items-start gap-3 p-6">
        <ErrorState role="alert">We could not load the logs. Check your connection, then try again.</ErrorState>
        <Btn variant="secondary" size="sm" onClick={() => load(page)}>Try again</Btn>
      </div>
    );
  } else if (logs.length === 0) {
    body = (
      <EmptyState className="m-6">
        {searchQuery ? 'No log entries match this search.' : 'No activity has been logged yet.'}
      </EmptyState>
    );
  } else {
    body = (
      <div className="max-h-[70vh] w-full max-w-full overflow-auto">
        <table aria-label="Logs" className="w-full min-w-[880px] text-left">
          <thead className="sticky top-0 z-10 bg-bm-subtle">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`whitespace-nowrap border-b border-bm-border px-2.5 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted ${cellWidth(header.column.id)}`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b border-bm-border bg-white transition-colors hover:bg-bm-subtle">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`overflow-hidden text-ellipsis whitespace-nowrap px-2.5 py-2.5 bm-body-m text-bm-ink ${cellWidth(cell.column.id)}`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        eyebrow="Admin Mode"
        title="Logs"
        subtitle="Every create, update and delete request made through the API, newest first."
      />

      <Card padding="p-5" className="mb-4 w-full min-w-0 max-w-full">
        <div className="flex flex-wrap items-center gap-3">
          <form
            role="search"
            aria-label="Filter logs"
            className="flex min-w-[240px] flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchQuery(inputValue);
            }}
          >
            <SearchBox
              value={inputValue}
              onChange={setInputValue}
              placeholder="Search by email, action or endpoint, then press Enter"
              aria-label="Search logs"
              className="min-w-0 flex-1"
            />
            <Btn type="submit" variant="secondary">Search</Btn>
          </form>
          <label className="flex items-center gap-2 bm-body-s text-bm-muted">
            Rows per page
            <Select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="!w-auto"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </Select>
          </label>
        </div>
      </Card>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="bm-body-s text-bm-muted">{summary}</p>
        {status !== 'error' && renderPagination()}
      </div>

      <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-hidden">
        {body}
      </Card>
    </div>
  );
};

export default LogViewer;
